<template>
  <div class="ratings-given-tab">
    <!-- Ratings Given to Client Table -->
    <div class="card-header py-3 px-0">
      <div class="card-title flex-column" style="justify-content: space-evenly">
        <h3 class="card-label font-weight-bolder text-dark m-0">
          Ratings Given to Client
        </h3>
      </div>
    </div>
    <div class="table-responsive mb-0">
      <table class="table table-head-custom table-vertical-center table-head-bg table-borderless">
        <thead>
          <tr class="text-left">
            <th>Target (Client)</th>
            <th>Rating Date</th>
            <th>Rating</th>
            <th>Tags</th>
            <th>Review</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedClientRatings" :key="'client-' + item.id">
            <td>
              <a :href="`/admin/client/${item.targetId}`" target="_blank">
                {{ item.targetName }}
              </a>
            </td>
            <td>{{ item.ratingDate }}</td>
            <td>
              <v-rating
                v-model="item.rating"
                readonly
                dense
                small
                color="amber"
                length="5"
                empty-icon="mdi-star"
                full-icon="mdi-star"
                background-color="#E0E0E0"
              ></v-rating>
            </td>
            <td>
              <v-chip
                v-for="(tag, idx) in item.tags"
                :key="'client-tag-' + idx"
                color="primary"
                class="ma-1"
                small
                outlined
              >
                {{ tag }}
              </v-chip>
            </td>
            <td>
              <v-btn
                depressed
                color="primary"
                class="mr-2"
                @click="viewReview(item)"
              >
                View Review
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Ratings Given to Colleagues Table -->
    <div class="card-header py-3 px-0 mt-8">
      <div class="card-title flex-column" style="justify-content: space-evenly">
        <h3 class="card-label font-weight-bolder text-dark m-0">
          Ratings Given to Colleagues
        </h3>
      </div>
    </div>
    <div class="table-responsive mb-0">
      <table class="table table-head-custom table-vertical-center table-head-bg table-borderless">
        <thead>
          <tr class="text-left">
            <th>Target (Teammate)</th>
            <th>Rating Date</th>
            <th>Rating</th>
            <th>Tags</th>
            <th>Review</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedColleagueRatings" :key="'colleague-' + item.id">
            <td>
              <a :href="`/admin/talent/${item.targetId}`" target="_blank">
                {{ item.targetName }}
              </a>
            </td>
            <td>{{ item.ratingDate }}</td>
            <td>
              <v-rating
                v-model="item.rating"
                readonly
                dense
                small
                color="amber"
                length="5"
                empty-icon="mdi-star"
                full-icon="mdi-star"
                background-color="#E0E0E0"
              ></v-rating>
            </td>
            <td>
              <v-chip
                v-for="(tag, idx) in item.tags"
                :key="'colleague-tag-' + idx"
                color="primary"
                class="ma-1"
                small
                outlined
              >
                {{ tag }}
              </v-chip>
            </td>
            <td>
              <v-btn
                depressed
                color="primary"
                class="mr-2"
                @click="viewReview(item)"
              >
                View Review
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Review Modal -->
    <v-dialog v-model="reviewDialog" max-width="600px">
      <v-card>
        <v-card-title>Review Details</v-card-title>
        <v-card-text>
          <div v-if="selectedReview">
            <div class="mb-4">
              <strong>Target:</strong>
              <a :href="selectedReview.targetType === 'client' ? `/admin/client/${selectedReview.targetId}` : `/admin/talent/${selectedReview.targetId}`" target="_blank">
                {{ selectedReview.targetName }}
              </a>
            </div>
            <div class="mb-4">
              <strong>Rating Date:</strong> {{ selectedReview.ratingDate }}
            </div>
            <div class="mb-4">
              <strong>Rating:</strong>
              <v-rating
                v-model="selectedReview.rating"
                readonly
                dense
                color="amber"
                :hover="false"
                length="5"
                show-label
                 empty-icon="mdi-star"
                full-icon="mdi-star"
                background-color="#E0E0E0"
                :labels="['Worst', 'Bad', 'Neutral', 'Good', 'Excellent']"
                class="ma-2"
              ></v-rating>
            </div>
            <div class="mb-4">
              <strong>Tags:</strong>
              <v-chip
                v-for="(tag, idx) in selectedReview.tags"
                :key="'modal-tag-' + idx"
                color="primary"
                class="ma-1"
                small
                outlined
              >
                {{ tag }}
              </v-chip>
            </div>
            <div>
              <strong>Review:</strong>
              <p>{{ selectedReview.review }}</p>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="reviewDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
export default {
  name: 'RatingsGivenTab',
  props: {
    talentId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      reviewDialog: false,
      selectedReview: null,
      // Dummy data for Ratings Given to Client
      clientRatings: [
        {
          id: 1,
          targetType: 'client',
          targetId: 501,
          targetName: 'Acme Corp',
          ratingDate: '2024-03-15',
          rating: 4,
          tags: ['Responsive', 'Clear Requirements'],
          review: 'Client was responsive and provided clear requirements.',
        },
        {
          id: 2,
          targetType: 'client',
          targetId: 502,
          targetName: 'Globex Inc',
          ratingDate: '2024-03-12',
          rating: 5,
          tags: ['Great Collaboration'],
          review: 'Excellent collaboration and support from the client.',
        },
      ],
      // Dummy data for Ratings Given to Colleagues
      colleagueRatings: [
        {
          id: 1,
          targetType: 'talent',
          targetId: 103,
          targetName: 'Mike Johnson',
          ratingDate: '2024-03-10',
          rating: 5,
          tags: ['Team Player', 'Helpful'],
          review: 'Great team player and always willing to help.',
        },
        {
          id: 2,
          targetType: 'talent',
          targetId: 104,
          targetName: 'Emily Clark',
          ratingDate: '2024-03-08',
          rating: 4,
          tags: ['Proactive'],
          review: 'Very proactive and supportive teammate.',
        },
      ],
    };
  },
  computed: {
    paginatedClientRatings() {
      return this.clientRatings;
    },
    paginatedColleagueRatings() {
      return this.colleagueRatings;
    },
  },
  methods: {
    viewReview(item) {
      this.selectedReview = item;
      this.reviewDialog = true;
    },
  },
};
</script>
<style scoped>
.ratings-given-tab {
  padding: 1rem;
}
.table-responsive {
  margin-bottom: 0;
}
.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #181c32;
  border-collapse: collapse;
}
.table th {
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.1rem;
  padding: 0.75rem 1rem;
  background-color: #f3f6f9;
  border-bottom: 1px solid #ebedf3;
}
.table td {
  padding: 0.75rem 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #ebedf3;
}
.table-borderless td,
.table-borderless th {
  border: 0;
}
.table-head-bg th {
  background-color: #f3f6f9;
}
.table-vertical-center td {
  vertical-align: middle;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  flex-wrap: wrap;
  min-height: 70px;
  padding: 0 2.25rem;
  background-color: transparent;
  border-bottom: 1px solid #ebedf3;
}
.card-title {
  display: flex;
  margin: 0.5rem;
  margin-left: 0;
  flex-wrap: wrap;
  align-items: center;
}
.card-label {
  display: flex;
  align-items: center;
  margin: 0;
  margin-left: 0.75rem;
  margin-right: 0.5rem;
  font-weight: 500;
  color: #181c32;
  font-size: 1.275rem;
}
</style> 